import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Typography,
  TextField,
  MenuItem,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery,
  CardActionArea,
  CircularProgress,
} from '@mui/material';
import { useState, useEffect } from 'react';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PublicIcon from '@mui/icons-material/Public';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import { motion } from 'framer-motion';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import { useAuth } from '../contexts/AuthContext'; // adjust path accordingly
import api from '../utils/api'; // your axios instance; adjust path if needed

const steps = ['Account Type', 'Account Info', 'Secure Account', 'Agree & Submit'];

export default function RegisterStepper() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const [activeStep, setActiveStep] = useState(0);
  const [selectedAccountType, setSelectedAccountType] = useState('savings');
  const [countries, setCountries] = useState([
    // initial fallback (keeps UI working before API responds)
    { code: 'NG', name: 'Nigeria', dial: '+234', currency_code: 'NGN' },
    { code: 'US', name: 'United States', dial: '+1', currency_code: 'USD' },
    { code: 'GB', name: 'United Kingdom', dial: '+44', currency_code: 'GBP' },
  ]);
  const [loadingCountries, setLoadingCountries] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    username: '',
    email: '',
    phone: '',
    country: 'NG',
    password: '',
    confirmPassword: '',
    agree: false,
  });
  const [loading, setLoading] = useState(false);

  // Snackbar states
  const [error, setError] = useState('');
  const [errorOpen, setErrorOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);

  const { register } = useAuth();

  // selectedCountry normalized to { code, name, dial, currency_code }
  const selectedCountry = countries.find((c) => {
    const cCode = (c.code || c.iso_code || c.iso || '').toUpperCase();
    return cCode === (formData.country || '').toUpperCase();
  });

  const handleChange = (field) => (e) =>
    setFormData({ ...formData, [field]: e.target.value });
  const handleCheckbox = (e) =>
    setFormData({ ...formData, agree: e.target.checked });

  const showError = (msg) => {
    setError(msg);
    setErrorOpen(true);
  };

  const validateStep = () => {
    if (activeStep === 1) {
      if (!formData.name || !formData.username || !formData.email || !formData.phone) {
        showError('All fields in Account Info are required');
        return false;
      }
    }
    if (activeStep === 2) {
      if (
        !formData.password ||
        formData.password.length < 8 ||
        formData.password !== formData.confirmPassword
      ) {
        showError('Passwords must match and be at least 8 characters');
        return false;
      }
    }
    if (activeStep === 3 && !formData.agree) {
      showError('You must agree to the terms');
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep()) setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    if (activeStep > 0) setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;

    const payload = {
      name: formData.name,
      full_name: formData.name,
      username: formData.username,
      email: formData.email,
      phone: formData.phone,
      country: formData.country,
      country_code: selectedCountry?.dial || '',
      password: formData.password,
      password_confirmation: formData.confirmPassword,
      account_type: selectedAccountType,
    };

    try {
      setLoading(true);
      await register(payload);

      setSuccessOpen(true); // show success snackbar

      setFormData({
        name: '',
        username: '',
        email: '',
        phone: '',
        country: 'NG',
        password: '',
        confirmPassword: '',
        agree: false,
      });
      setActiveStep(0);

      // keep the redirect but close snackbar quickly to let user see it
      setTimeout(() => {
        window.location.href = '/user/dashboard';
      }, 1400);
    } catch (err) {
      console.error('Registration error:', err);

      if (err?.response) {
        if (err.response.status === 422 && err.response.data?.errors) {
          const firstError = Object.values(err.response.data.errors)[0][0];
          showError(firstError);
        } else if (err.response.data?.message) {
          showError(err.response.data.message);
        } else {
          showError('Something went wrong. Please try again.');
        }
      } else if (err?.request) {
        showError('Server is not responding. Please check your connection.');
      } else {
        showError('An unexpected error occurred.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch countries from backend on mount
  useEffect(() => {
    let mounted = true;
    setLoadingCountries(true);
    api
      .get('/api/countries')
      .then((res) => {
        if (!mounted) return;
        const data = res.data?.data || res.data || [];
        // Map backend fields into the component-friendly shape { code, name, dial, currency_code }
        const mapped = data.map((item) => ({
          code: (item.iso_code || item.code || item.country_code || item.alpha2 || '').toUpperCase(),
          name: item.name || item.country || item.title || '',
          dial: item.phone_code || item.calling_code || item.dial || item.phone || '',
          currency_code: item.currency_code || item.currency || item.currencyCode || null,
        }));
        // Filter out entries that don't have code or dial (defensive)
        const cleaned = mapped.filter((c) => c.code && c.dial);
        if (cleaned.length > 0) {
          setCountries(cleaned);
        }
      })
      .catch((err) => {
        console.error('Failed to load countries:', err);
        // Keep fallback if API fails
      })
      .finally(() => setLoadingCountries(false));
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <Box
      minHeight="100vh"
      display="flex"
      justifyContent="center"
      alignItems="center"
      sx={{
        background: 'radial-gradient(ellipse at top left, #0ea5e9 0%, #0f172a 100%)',
        px: 2,
      }}
    >
      <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
        <Card
          sx={{
            width: isMobile ? '100%' : 500,
            maxWidth: '90vw',
            p: isMobile ? 1.5 : 3,
            borderRadius: 4,
            background: 'rgba(30, 41, 59, 0.95)',
            boxShadow: '0 0 40px 0 #00b4d8, 0 2px 20px 0 #0ea5e9',
          }}
        >
          <CardContent>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel sx={{ color: 'white', '& .MuiStepLabel-label': { color: 'white' } }}>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {activeStep === 0 && (
              <Box mt={isMobile ? 2 : 3}>
                <Typography variant={isMobile ? 'subtitle1' : 'h6'} color="#fff" mb={2} fontWeight="bold" textAlign="center">
                  Choose your account type
                </Typography>
                <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} gap={3} justifyContent="center" alignItems="center">
                  <motion.div whileHover={{ scale: 1.05, boxShadow: '0 0 32px 8px #00b4d8' }} whileTap={{ scale: 0.98 }}>
                    <CardActionArea
                      onClick={() => setSelectedAccountType('savings')}
                      sx={{
                        borderRadius: 4,
                        boxShadow:
                          selectedAccountType === 'savings'
                            ? '0 0 24px 4px #00b4d8, 0 0 0 2px #7B2FF2 inset'
                            : '0 0 12px 0 #00b4d8',
                        border: selectedAccountType === 'savings' ? '2px solid #7B2FF2' : '2px solid transparent',
                        background: selectedAccountType === 'savings' ? 'linear-gradient(135deg, #0ea5e9 60%, #7B2FF2 100%)' : 'rgba(30,41,59,0.85)',
                        transition: 'all 0.2s',
                        width: isMobile ? '100%' : 210,
                        mb: isMobile ? 2 : 0,
                        p: 2,
                      }}
                    >
                      <Box display="flex" flexDirection="column" alignItems="center" py={2}>
                        <SavingsOutlinedIcon sx={{ fontSize: 54, color: '#00B4D8', mb: 1, filter: 'drop-shadow(0 0 8px #00B4D8)' }} />
                        <Typography variant="h6" color="#fff" fontWeight="bold" mb={0.5}>
                          Savings Account
                        </Typography>
                        <Typography variant="body2" color="#b0b8c1" textAlign="center">
                          Best for individuals looking to save money securely.
                        </Typography>
                      </Box>
                    </CardActionArea>
                  </motion.div>

                  <motion.div whileHover={{ scale: 1.05, boxShadow: '0 0 32px 8px #7B2FF2' }} whileTap={{ scale: 0.98 }}>
                    <CardActionArea
                      onClick={() => setSelectedAccountType('current')}
                      sx={{
                        borderRadius: 4,
                        boxShadow:
                          selectedAccountType === 'current'
                            ? '0 0 24px 4px #7B2FF2, 0 0 0 2px #00b4d8 inset'
                            : '0 0 12px 0 #7B2FF2',
                        border: selectedAccountType === 'current' ? '2px solid #00b4d8' : '2px solid transparent',
                        background: selectedAccountType === 'current' ? 'linear-gradient(135deg, #7B2FF2 60%, #00B4D8 100%)' : 'rgba(30,41,59,0.85)',
                        transition: 'all 0.2s',
                        width: isMobile ? '100%' : 210,
                        mb: isMobile ? 2 : 0,
                        p: 2,
                      }}
                    >
                      <Box display="flex" flexDirection="column" alignItems="center" py={2}>
                        <BusinessCenterIcon sx={{ fontSize: 54, color: '#7B2FF2', mb: 1, filter: 'drop-shadow(0 0 8px #7B2FF2)' }} />
                        <Typography variant="h6" color="#fff" fontWeight="bold" mb={0.5}>
                          Current Account
                        </Typography>
                        <Typography variant="body2" color="#b0b8c1" textAlign="center">
                          Ideal for frequent transactions and business use.
                        </Typography>
                      </Box>
                    </CardActionArea>
                  </motion.div>
                </Box>
                <Typography variant="body2" color="#b0b8c1" mt={2} textAlign="center">
                  Select the account type that best fits your needs. You can always update your profile later.
                </Typography>
              </Box>
            )}

            {activeStep === 1 && (
              <Box mt={isMobile ? 2 : 3}>
                <Typography variant="subtitle2" color="#7B2FF2" fontWeight="bold" textAlign="center" mb={1} sx={{ letterSpacing: 1, fontSize: isMobile ? 14 : 16 }}>
                  Step 2 of 3
                </Typography>
                <Typography variant={isMobile ? 'h6' : 'h5'} color="#fff" fontWeight="bold" textAlign="center" mb={0.5}>
                  Account Info Setup
                </Typography>
                <Typography variant="body2" color="#b0b8c1" textAlign="center" mb={2}>
                  Please provide your details to get started.
                </Typography>

                <TextField
                  fullWidth
                  label="Full Name"
                  value={formData.name}
                  onChange={handleChange('name')}
                  margin="normal"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon color="primary" />
                      </InputAdornment>
                    ),
                  }}
                  sx={fieldStyle}
                />
                <TextField
                  fullWidth
                  label="Username"
                  value={formData.username}
                  onChange={handleChange('username')}
                  margin="normal"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon color="primary" />
                      </InputAdornment>
                    ),
                  }}
                  sx={fieldStyle}
                />
                <TextField
                  fullWidth
                  label="Email"
                  value={formData.email}
                  onChange={handleChange('email')}
                  margin="normal"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon color="primary" />
                      </InputAdornment>
                    ),
                  }}
                  sx={fieldStyle}
                />

                <TextField
                  select
                  fullWidth
                  label="Country"
                  value={formData.country}
                  onChange={(e) => {
                    handleChange('country')(e);
                    // optionally update phone prefix if desired
                  }}
                  margin="normal"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PublicIcon color="primary" />
                      </InputAdornment>
                    ),
                  }}
                  sx={fieldStyle}
                >
                  {loadingCountries ? (
                    <MenuItem disabled>
                      <Box display="flex" alignItems="center" gap={1}>
                        <CircularProgress size={18} />
                        <Typography>Loading countries...</Typography>
                      </Box>
                    </MenuItem>
                  ) : (
                    countries.map((c) => (
                      <MenuItem key={c.code} value={c.code}>
                        {c.name} ({c.dial})
                      </MenuItem>
                    ))
                  )}
                </TextField>

                <Box display="flex" gap={1} flexDirection={isMobile ? 'column' : 'row'}>
                  <TextField disabled label="Code" value={selectedCountry?.dial || ''} sx={{ width: isMobile ? '100%' : 100, ...fieldStyle }} />
                  <TextField
                    fullWidth
                    label="Mobile"
                    value={formData.phone}
                    onChange={handleChange('phone')}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIphoneIcon color="primary" />
                        </InputAdornment>
                      ),
                    }}
                    sx={fieldStyle}
                  />
                </Box>
              </Box>
            )}

            {activeStep === 2 && (
              <Box mt={isMobile ? 2 : 3}>
                <Typography variant="subtitle2" color="#7B2FF2" fontWeight="bold" textAlign="center" mb={1} sx={{ letterSpacing: 1, fontSize: isMobile ? 14 : 16 }}>
                  Step 3 of 4
                </Typography>
                <Typography variant={isMobile ? 'h6' : 'h5'} color="#fff" fontWeight="bold" textAlign="center" mb={0.5}>
                  Secure Account
                </Typography>
                <Typography variant="body2" color="#b0b8c1" textAlign="center" mb={2}>
                  Create a strong password to protect your account.
                </Typography>
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange('password')}
                  margin="normal"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon color="primary" />
                      </InputAdornment>
                    ),
                  }}
                  sx={fieldStyle}
                />
                <TextField
                  fullWidth
                  label="Confirm Password"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange('confirmPassword')}
                  margin="normal"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon color="primary" />
                      </InputAdornment>
                    ),
                  }}
                  sx={fieldStyle}
                />
                <Typography variant="body2" color="#b0b8c1" textAlign="left" mt={1}>
                  Use at least 8 characters, including a number and a symbol.
                </Typography>
              </Box>
            )}

            {activeStep === 3 && (
              <Box mt={isMobile ? 2 : 3}>
                <Typography variant="subtitle2" color="#7B2FF2" fontWeight="bold" textAlign="center" mb={1} sx={{ letterSpacing: 1, fontSize: isMobile ? 14 : 16 }}>
                  Step 4 of 4
                </Typography>
                <Typography variant={isMobile ? 'h6' : 'h5'} color="#fff" fontWeight="bold" textAlign="center" mb={0.5}>
                  Agree & Submit
                </Typography>
                <Typography variant="body2" color="#b0b8c1" textAlign="center" mb={2}>
                  Please agree to our Terms and Policies to your registration.
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.agree}
                      onChange={handleCheckbox}
                      sx={{
                        color: '#00B4D8',
                        '&.Mui-checked': { color: '#7B2FF2' },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" color="#fff">
                      I agree to the{' '}
                      <Button
                        variant="text"
                        sx={{ color: '#00B4D8', textTransform: 'none', p: 0 }}
                        onClick={() => navigate('/terms-condition-policy')}
                      >
                        Terms and Policies
                      </Button>
                    </Typography>
                  }
                  sx={{ mb: 2 }}
                />
                <Typography variant="caption" color="#b0b8c1" textAlign="center" display="block">
                  By submitting, you agree to our terms of service and privacy policy.
                </Typography>
              </Box>
            )}

            <Box display="flex" justifyContent="space-between" mt={3}>
              <Button disabled={activeStep === 0} onClick={handleBack} variant="text" sx={{ color: '#00B4D8' }}>
                Back
              </Button>

              {activeStep < steps.length - 1 ? (
                <Button onClick={handleNext} variant="contained" sx={buttonStyle}>
                  Next
                </Button>
              ) : (
                <Button onClick={handleSubmit} variant="contained" sx={buttonStyle} disabled={loading}>
                  {loading ? 'Submitting...' : 'Submit'}
                </Button>
              )}
            </Box>

            {/* Error Snackbar (strong red) */}
            <Snackbar open={errorOpen} autoHideDuration={4000} onClose={() => setErrorOpen(false)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
              <Alert
                severity="error"
                variant="filled"
                onClose={() => setErrorOpen(false)}
                sx={{
                  backgroundColor: '#dc2626',
                  color: '#fff',
                  '& .MuiAlert-icon': { color: '#fff' },
                }}
              >
                {error}
              </Alert>
            </Snackbar>

            {/* Success Snackbar (strong green) */}
            <Snackbar open={successOpen} autoHideDuration={3000} onClose={() => setSuccessOpen(false)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
              <Alert
                severity="success"
                variant="filled"
                onClose={() => setSuccessOpen(false)}
                sx={{
                  backgroundColor: '#059669',
                  color: '#fff',
                  '& .MuiAlert-icon': { color: '#fff' },
                }}
              >
                Registration successful! Redirecting...
              </Alert>
            </Snackbar>
          </CardContent>
        </Card>
      </motion.div>
    </Box>
  );
}

const buttonStyle = {
  py: 1.2,
  px: 4,
  fontWeight: 'bold',
  fontSize: 16,
  background: 'linear-gradient(135deg, #00B4D8, #7B2FF2)',
  boxShadow: '0 0 16px 2px #00b4d8',
  borderRadius: 3,
  '&:hover': {
    background: 'linear-gradient(135deg, #7B2FF2, #00B4D8)',
    boxShadow: '0 0 32px 4px #7B2FF2',
  },
};

const fieldStyle = {
  input: { color: '#fff' },
  label: { color: '#b0b8c1' },
  fieldset: { borderColor: '#00b4d8' },
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: '#00b4d8' },
    '&:hover fieldset': { borderColor: '#7B2FF2' },
    '&.Mui-focused fieldset': { borderColor: '#7B2FF2' },
  },
  mb: 2,
};